import React, { useRef, useState, useMemo } from "react";
import { Form, Row, Col, Space, Divider, Tooltip, Modal } from "antd";
import { useDispatch } from "react-redux";

import { superAppActions } from "@/redux/actions";
import { useNotification } from "@/hooks";
import { cleanObject } from "@/utils";
import { DropdownField, TextField, UploadImageImmediatelyField } from "@/components/Form";

import { EyeInvisibleOutlined, CopyOutlined, RedoOutlined, EyeOutlined } from "@ant-design/icons";
import { appPlatformDDL } from "@/constants/masterData";

const { confirm } = Modal;

const AppForm = ({ form, formId, detailData, isCreating, onSubmit }) => {
    const dispatch = useDispatch();
    const { showErrorMessage, showSuccessMessage } = useNotification();
    const clientAppRef = useRef(null);
    const secretRef = useRef(null);
    const iconUrlRef = useRef(null);

    const [arrHidden, setArrHidden] = useState([]);

    const handleSubmit = (values) => {
        console.log({ values });
        onSubmit(cleanObject(values));
    };

    const refreshAppToken = () => {
        dispatch(
            superAppActions.generateToken({
                params: { id: detailData.id },
                onCompleted: (res) => {
                    form.setFieldsValue(res.data);
                    showSuccessMessage("Renew token successfully");
                },
                onError: (err) => {
                    console.log({ err });
                    showErrorMessage("Renew token failed");
                },
            })
        );
    };

    const onRefresh = () => {
        confirm({
            title: `Are you sure to revoke Token?`,
            okText: "Yes",
            cancelText: "No",
            okType: "danger",
            centered: true,
            onOk: () => {
                return refreshAppToken();
            },
        });
    };

    const addOnAfter = (index, ref, isRefresh = true) => {
        return (
            <Space>
                {arrHidden.includes(index) ? (
                    <EyeOutlined style={{ cursor: "pointer" }} onClick={() => togglePassword(index)} />
                ) : (
                    <EyeInvisibleOutlined style={{ cursor: "pointer" }} onClick={() => togglePassword(index)} />
                )}
                <Divider type="vertical" />
                <Tooltip trigger="click" title="copied">
                    <CopyOutlined style={{ cursor: "pointer" }} onClick={() => copyText(ref)} />
                </Tooltip>
                {isRefresh ? (
                    <>
                        <Divider type="vertical" />{" "}
                        <RedoOutlined style={{ cursor: "pointer" }} onClick={() => onRefresh()} />
                    </>
                ) : null}
            </Space>
        );
    };

    const togglePassword = (index) => {
        if (arrHidden.includes(index)) {
            setArrHidden(arrHidden.filter((item) => item !== index));
        } else {
            setArrHidden([...arrHidden, index]);
        }
    };

    const copyText = (ref) => {
        console.log({ ref });
        const { input, setSelectionRange } = ref.current;
        if (ref.current) {
            setSelectionRange(0, 99999);
            navigator.clipboard?.writeText?.(input.value);
        }
    };

    const defaultImage = useMemo(() => {
        return detailData?.imageUrl ? [{ uid: detailData?.id, url: detailData?.imageUrl }] : [];
    }, [detailData?.imageUrl]);

    const onSuccessUpload = (res) => {
        if (res.result) {
            showSuccessMessage(res.message);
        } else {
            showErrorMessage(res.message);
        }
    };

    return (
        <Form id={formId} onFinish={handleSubmit} form={form} layout="vertical" initialValues={detailData}>
            <Row gutter={16}>
                <Col span={24}>
                    <DropdownField fieldName="appPlatform" label="App Platform" required options={appPlatformDDL} />
                </Col>
                <Col span={24}>
                    <TextField fieldName="appName" label="Tên Mini app" required />
                </Col>
                <Col span={24}>
                    <UploadImageImmediatelyField
                        label="Logo"
                        fieldName="imageUrl"
                        objectId={detailData?.id}
                        maxCount={1}
                        defaultValue={defaultImage}
                        uploadApiAction={superAppActions.uploadImage}
                        onSuccessUpload={onSuccessUpload}
                        required={!isCreating}
                        hidden={isCreating}
                    />
                </Col>
                <Col span={24}>
                    <TextField
                        fieldName="imageUrl"
                        disabled
                        label="Link icon"
                        readOnly={true}
                        insideRef={iconUrlRef}
                        addonAfter={
                            <Tooltip trigger="click" title="copied">
                                <CopyOutlined style={{ cursor: "pointer" }} onClick={() => copyText(iconUrlRef)} />
                            </Tooltip>
                        }
                        hidden={isCreating}
                    />
                </Col>
                <Col span={24}>
                    <TextField
                        type={arrHidden.includes(2) ? "password" : "text"}
                        fieldName="appId"
                        label="ID"
                        hidden={isCreating}
                        disabled
                        readOnly={true}
                        insideRef={clientAppRef}
                        addonAfter={addOnAfter(2, clientAppRef, false)}
                    />
                </Col>
                <Col span={24}>
                    <TextField
                        type={arrHidden.includes(3) ? "password" : "text"}
                        fieldName="appToken"
                        label="Token"
                        hidden={isCreating}
                        readOnly={true}
                        insideRef={secretRef}
                        addonAfter={addOnAfter(3, secretRef)}
                    />
                </Col>
            </Row>
        </Form>
    );
};

export default React.memo(AppForm);
